import { generateClient } from "aws-amplify/api";
import { AuthUser } from "aws-amplify/auth";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { User } from "~/api/API";
import { usersByCognitoUserId } from "~/api/graphql-custom/queries";
import { isAuthCredentialError } from "~/auth/authErrors";
import { getCurrentEnv } from "~/common/utils";
import { errorReporter } from "~/error-handling";
import { setGlobalUserContextForCorrelation } from "~/error-handling/correlationContext";

const client = generateClient({ authMode: "userPool" });

interface UserContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  refreshUser?: () => Promise<void>;
  /**
   * Whether the current user is a demo user.
   * Derived from the user's associated Doctor or MedicalAssistant record.
   * Demo users should only see demo appointments, and non-demo users should not see demo data.
   */
  isDemo: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  authUser: AuthUser | null | undefined;
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({
  authUser,
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Which Cognito identity the `user` above was fetched for. A failed fetch
  // only sets `error`, so without this a record loaded for one identity would
  // keep being served after `authUser` changed — and `user.id` is threaded into
  // writes, so that is a wrong-attribution risk, not just a stale display.
  const loadedForUserIdRef = useRef<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!authUser?.userId) {
      setUser(null);
      setLoading(false);
      // Clear global user context and session context when no user
      setGlobalUserContextForCorrelation(null);
      errorReporter.setSessionContext("anonymous", getCurrentEnv());
      return;
    }

    // Identity changed: drop the previous account's record before fetching, so
    // a failure can't leave it exposed under the new identity.
    if (loadedForUserIdRef.current !== authUser.userId) {
      loadedForUserIdRef.current = null;
      setUser(null);
    }

    setLoading(true);
    setError(null);

    try {
      // Add breadcrumb for user data fetching attempt
      errorReporter.addFeatureBreadcrumb("user-data-fetch", "started", {
        authUserId: authUser.userId,
      });

      const userResponse = await client.graphql({
        query: usersByCognitoUserId,
        variables: { cognitoUserId: authUser.userId },
      });

      const items = userResponse.data?.usersByCognitoUserId?.items || [];

      if (items.length === 0) throw new Error("User data not found");
      if (items.length > 1) {
        console.warn("Multiple users found for cognito user id", items);
      }

      const fetchedUser = items[0] || null;
      setUser(fetchedUser as User);
      loadedForUserIdRef.current = authUser.userId;

      // Set global user context for correlation context detection
      if (fetchedUser) {
        setGlobalUserContextForCorrelation({
          id: fetchedUser.id,
        });
      }

      // Set session context for error reporting
      const sessionId = `session_${authUser.userId}_${Date.now()}`;
      errorReporter.setSessionContext(sessionId, getCurrentEnv());

      // Add breadcrumb for successful user data fetch
      errorReporter.addFeatureBreadcrumb("user-data-fetch", "completed", {
        authUserId: authUser.userId,
        hasUserData: !!fetchedUser,
        userCount: items.length,
      });
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("An unknown error occurred");

      // Report user fetching errors with appropriate context
      errorReporter.reportApplicationError(error, {
        tags: {
          component: "UserProvider",
          operation: "fetch_user_data",
          service: "graphql",
          query: "usersByCognitoUserId",
          errorCategory: "user_data_error",
        },
        extra: {
          authUserId: authUser?.userId,
          hasAuthUser: !!authUser,
          errorType: err instanceof Error ? err.constructor.name : "Unknown",
          errorName: error.name,
          errorMessage: error.message,
        },
      });

      console.error("Error fetching user:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  // This throw escapes to the *top-level* boundary, replacing the entire app —
  // navbar, banners and all — with the generic fallback. For an auth error that
  // is precisely wrong: the session is recoverable, SessionExpiredBanner is
  // what the user needs to see, and it lives inside the subtree this would
  // unmount. A previously-loaded user also makes it unnecessary; consumers read
  // `user` from context and can keep working with what we already have.
  //
  // A first load failing for a non-auth reason is still fatal — nothing
  // downstream can function without a user record — so that case still throws.
  if (error && !user && !isAuthCredentialError(error)) {
    throw error;
  }

  // Compute isDemo from the user's doctor or assistant record
  const isDemo = Boolean(user?.doctor?.isDemo || user?.assistant?.isDemo);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        error,
        refreshUser: fetchUser,
        isDemo,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useCurrentUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    const error = new Error(
      "useCurrentUser must be used within a UserProvider",
    );

    // Report context usage errors
    errorReporter.reportApplicationError(error, {
      tags: {
        component: "UserProvider",
        operation: "use_current_user_hook",
        errorType: "context_usage_error",
        errorCategory: "react_error",
      },
      extra: {
        hookName: "useCurrentUser",
        contextAvailable: !!context,
      },
    });

    throw error;
  }
  return context;
};
